import { Injectable } from '@angular/core';
import posthog from 'posthog-js';
import { environment } from 'src/environments/environment';
import { Account } from '../models/account';
import { Organization } from '../models/organization';
import { redactCaptureResult } from '../helpers/analytics-redact';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private initialized = false;

  init(): void {
    if (this.initialized || !environment.POSTHOG_API_KEY) {
      return;
    }
    posthog.init(environment.POSTHOG_API_KEY, {
      api_host: environment.POSTHOG_HOST,
      capture_pageview: false,
      autocapture: false,
      persistence: 'localStorage',
      // Search text in the address (q, abQ, …) is redacted on every event's URL properties, person ones too
      before_send: redactCaptureResult,
    });
    posthog.register({
      app_environment: environment.APP_ENV,
    });
    this.initialized = true;
  }

  identify(user: Account, org?: Organization): void {
    if (!this.initialized) return;
    posthog.identify(user.id, {
      role: user.role,
    });
    if (org) {
      posthog.group('organization', org.id, {
        name: org.name,
        type: org.type,
      });
    }
  }

  reset(): void {
    if (!this.initialized) return;
    posthog.reset();
  }

  setProjectContext(projectId: string, projectName: string): void {
    if (!this.initialized) return;
    posthog.register({
      project_id: projectId,
      project_name: projectName,
    });
  }

  clearProjectContext(): void {
    if (!this.initialized) return;
    posthog.unregister('project_id');
    posthog.unregister('project_name');
  }

  capture(eventName: string, properties?: Record<string, any>): void {
    if (!this.initialized) return;
    try {
      posthog.capture(eventName, properties);
    } catch (e) {
      console.warn('Analytics capture failed', e);
    }
  }
}
